/** Splitting a compound-tagged equipment into the assets its tag names.
 *
 *  A tag like "109-DA/DB" is one drawing symbol standing for several plant
 *  assets, which the register can only store as one asset that exists nowhere in
 *  the plant. The split renames the equipment to the first asset and creates the
 *  rest on the same drawing. Every surface that offers the split — the equipment
 *  edit panel and the plant hierarchy — runs it from here.
 */

import { pnidApi } from '../api';
import { apiErrorMessage } from './apiError';

/** One asset a compound tag is being split into. */
export interface CompoundSplitPart {
  equipment_no: string;
  description: string;
}

/** Two tags that differ only in case or separators name the same asset. */
export const normalizeTagForSplitComparison = (tag: string): string =>
  tag.trim().toUpperCase().replace(/[\s_-]+/g, '-');

export interface SplitCompoundEquipmentInput {
  /** The drawing that holds the equipment, and whose parts are written to it. */
  docId: string;
  equipmentId: string;
  /** The assets to split into, in drawing order. The first renames the original. */
  parts: CompoundSplitPart[];
  /**
   * Re-reads whatever the calling surface shows the drawing from. Run once the
   * split settles, and again on a partial failure so the reviewer sees the state
   * the run actually left behind.
   */
  refresh: () => Promise<void>;
}

/**
 * Splits a compound-tagged equipment into one asset per part: the first part
 * renames it, the rest become new equipment inheriting everything the parts
 * shared as one symbol. Where the symbol was this drawing's to define, each
 * part carries that claim only if the plant has no drawing defining that tag
 * already. Runs in order and stops at the first failure, so the caller is told
 * exactly how far it got — nothing is rolled back.
 *
 * Resolves with the tags written, in order. Rejects with the message to show the
 * reviewer, naming the tags the run had already saved.
 */
export async function splitCompoundEquipment({
  docId,
  equipmentId,
  parts,
  refresh,
}: SplitCompoundEquipmentInput): Promise<string[]> {
  const [renamedPart, ...newParts] = parts;
  if (!renamedPart) throw new Error('A split needs at least one asset.');

  // The record the new assets copy down from is read from the drawing rather
  // than from the surface that asked for the split: the copy-down carries the
  // symbol's instruments, connections and primary claim, and the three surfaces
  // that offer the split hold different amounts of the record between them.
  const { state: drawing } = await pnidApi.get(docId);
  const original = drawing.equipments.find((equipment) => equipment.id === equipmentId);
  if (!original) {
    throw new Error('This equipment is no longer on the drawing. Reload and split again.');
  }

  // A part naming an asset the drawing already carries would add a second
  // element for one tag on one sheet. The editor can only compare the parts
  // against each other, so the drawing's own tags are checked here, where the
  // record has just been read and before anything is written.
  const tagsAlreadyOnDrawing = new Set(
    drawing.equipments
      .filter((equipment) => equipment.id !== original.id)
      .map((equipment) => normalizeTagForSplitComparison(equipment.equipment_no ?? '')),
  );
  const partsAlreadyOnDrawing = parts
    .map((part) => part.equipment_no)
    .filter((tag) => tagsAlreadyOnDrawing.has(normalizeTagForSplitComparison(tag)));
  if (partsAlreadyOnDrawing.length > 0) {
    throw new Error(
      `This drawing already has ${partsAlreadyOnDrawing.join(', ')}. ` +
        'Rename or remove that part — nothing was saved.',
    );
  }

  // Which parts this drawing may claim to define. Marking a drawing primary
  // releases the mark from every other drawing in the plant, and that release
  // is not permission-checked — so a split claims a part only where the plant
  // has no drawing defining that tag yet. Where one exists, the part is written
  // without a claim and the panel's "Primary: <drawing>" line shows the
  // reviewer who holds it, to settle by hand.
  const partsThisDrawingMayClaim = new Set<string>();
  if (original.is_primary_source) {
    for (const part of parts) {
      let holder;
      try {
        holder = await pnidApi.getTagContext(docId, {
          tag: part.equipment_no,
          kind: 'equipment',
        });
      } catch (err) {
        throw new Error(
          `${apiErrorMessage(err)} — could not read who defines ${part.equipment_no}, ` +
            'so nothing was saved.',
        );
      }
      // This drawing holding the old compound tag is not a holder of the part
      // tags, and a tag this drawing already defines is its own to keep.
      if (holder.primary_source_file_id == null || holder.this_file_is_primary) {
        partsThisDrawingMayClaim.add(part.equipment_no);
      }
    }
  }

  const savedTags: string[] = [];
  try {
    await pnidApi.updateEquipment(docId, original.id, {
      equipment_no: renamedPart.equipment_no,
      description: renamedPart.description,
      // Renaming an element normally drops its primary mark, since the claim
      // was approved for the old tag. Where the new tag is undisputed the claim
      // is restated for it and rides through the rename; where another drawing
      // defines it, the field is left out and the rename drops the mark. The
      // owner is left out too, so the rename leaves the stored one standing.
      ...(partsThisDrawingMayClaim.has(renamedPart.equipment_no)
        ? { is_primary_source: true }
        : {}),
    });
    savedTags.push(renamedPart.equipment_no);
    for (const part of newParts) {
      // The new asset inherits everything the parts shared as one symbol —
      // including the instruments tagged on it and the peers it is connected
      // to, each of which the whole symbol carried and each part therefore
      // carries. Identity, review state and the cross-document links stay with
      // the original; the claim to define the asset is carried only where no
      // other drawing already holds it.
      await pnidApi.createEquipment(docId, {
        equipment_no: part.equipment_no,
        equipment_type: original.equipment_type,
        description: part.description,
        pnid_no: original.pnid_no ?? null,
        bbox: original.bbox ?? null,
        label_bbox: original.label_bbox ?? null,
        details: original.details ?? {},
        equipment_data: original.equipment_data ?? null,
        supporting_refs: original.supporting_refs ?? null,
        related_docs: original.related_docs,
        notes: original.notes ?? null,
        painting_spec: original.painting_spec ?? null,
        ai_instruments: original.ai_instruments ?? [],
        connected_equipments: original.connected_equipments ?? null,
        owner_domain_id: original.owner_domain_id,
        is_primary_source: partsThisDrawingMayClaim.has(part.equipment_no) ? true : null,
      });
      savedTags.push(part.equipment_no);
    }
  } catch (err) {
    // Part of the split may already be on the drawing — show the reviewer that
    // state rather than a stale one.
    await refresh().catch(() => undefined);
    const message = apiErrorMessage(err);
    throw new Error(
      savedTags.length > 0
        ? `${message} — saved: ${savedTags.join(', ')}. The remaining assets were not created.`
        : `${message} — nothing was saved.`,
    );
  }

  await refresh();
  return savedTags;
}
